import React, { memo, useCallback, useEffect, useState } from 'react';
import {
	EmailIcon,
	EmailShareButton,
	FacebookIcon,
	FacebookShareButton,
	TwitterIcon,
	TwitterShareButton,
	WhatsappIcon,
	WhatsappShareButton,
} from 'react-share';

import { SelectField } from './SelectField';
import { trackGroupBuy } from '../services/requests';
import { getCobuyerLabel, isPropertyRental } from '../config/constants';
import { IConfig, GroupbuyTransaction } from '../config/interfaces';
import { buildWidgetAnalyticsContext, postParentAnalyticsEvent } from '../analytics/widgetTracking';

type ShareProps = {
	options: { value: string; label: string }[];
	responseId: string;
	brandColor?: string;
	businessName?: string;
	publicKey: string;
	config: IConfig;
};

export const ShareForm = memo(({ responseId, publicKey, businessName, brandColor, config }: ShareProps) => {
	const [selectedBuyer, setSelectedBuyer] = useState<{ value: string; label: string }>();

	const [customers, setCustomers] = useState<GroupbuyTransaction | undefined>();

	const handleTrackData = useCallback(async () => {
		if (responseId) {
			const [data, error] = await trackGroupBuy(responseId);
			if (data) {
				const response: GroupbuyTransaction = data.groupbuyTransaction;

				setCustomers(response);
				return {
					...response,
				};
			}
			if (error) throw new Error('Something went wrong');
		}
		throw new Error('Something went wrong');
	}, [responseId]);

	useEffect(() => {
		handleTrackData();
	}, []);

	const options =
		customers?.groupbuy_transaction_customers
			.filter((customer) => customer.customer_id !== customers.primary_customer_id)
			.map((customer) => ({
				label: customer.customer?.first_name + ' ' + customer.customer?.last_name || '',
				value: customer.customer_id,
			})) || [];

	const selectedPaymentUrl = `${process.env.REACT_APP_COBUYR_PAYMENT}?transaction_id=${responseId}&pK=${publicKey}&customer_id=${selectedBuyer?.value}`;

	const customizeMessage = (name?: string) => {
		if (name) return `Hi ${name}, Here is the link to pay your amount for our group purchase.`;

		return isPropertyRental(config) ? 'Select a guest above' : 'Select a co-buyer above';
	};

	const [message, setMessage] = useState(customizeMessage(selectedBuyer?.label));

	const includePaymentUrl = message + ' ' + selectedPaymentUrl;
	const analyticsContext = buildWidgetAnalyticsContext(config);

	const trackShareClick = (channel: string) => {
		postParentAnalyticsEvent('cobuyr_share_clicked', {
			...analyticsContext,
			share_channel: channel,
			cobuyer_selected: Boolean(selectedBuyer),
		});
	};

	return (
		<div>
			{/* <h4 className="text-2xl font-medium">Send payment link to co-buyers</h4> */}

			{!isPropertyRental(config) && (
				<>
					<SelectField
						options={options}
						color={brandColor}
						className="w-full"
						onChange={(value) => {
							setSelectedBuyer(value);
							setMessage(customizeMessage(value.label));
						}}
						label={`Choose a ${getCobuyerLabel(config).toLowerCase()}`}
					/>
					<div className="mt-4">
						<form>
							<div className="w-full mb-4 border border-gray-200 rounded-lg bg-gray-50  ">
								<div className="px-4 py-2 bg-white rounded-t-lg ">
									<textarea
										rows={4}
										disabled={!selectedBuyer}
										onChange={(event) => setMessage(event.target.value)}
										value={message}
										className="w-full px-0 text-sm text-gray-900 bg-white border-0 focus:ring-0 "
										placeholder="Write a comment..."
									></textarea>
								</div>
								<div className="flex items-center justify-between px-3 py-2 border-t ">
									<button
										onClick={(event) => {
											event.preventDefault();
											navigator.clipboard.writeText(`${selectedPaymentUrl}`);
											trackShareClick('copy_link');
										}}
										disabled={!selectedBuyer}
										className={`inline-flex items-center py-2.5 px-4 text-xs font-medium text-center text-white ${
											brandColor ? `bg-[${brandColor}]` : 'bg-blue-700'
										}  rounded-lg  ${
											brandColor
												? ` hover:bg-[${brandColor}] focus:ring-4 focus:ring-[${brandColor}]`
												: ' hover:bg-blue-800 focus:ring-4 focus:ring-blue-200'
										} `}
									>
										Copy Link
									</button>
								</div>
							</div>
						</form>
					</div>
					<div className="mt-10 flex justify-between px-8">
						<EmailShareButton
							openShareDialogOnClick
							onClick={() => trackShareClick('email')}
							subject={`GROUP BUY from ${businessName}`}
							body={includePaymentUrl}
							disabled={!selectedBuyer}
							separator=""
							url={''}
						>
							<EmailIcon size={'32px'} round />
						</EmailShareButton>

						<FacebookShareButton
							title={includePaymentUrl}
							disabled={!selectedBuyer}
							url="https://web.facebook.com/"
							onClick={() => trackShareClick('facebook')}
						>
							<FacebookIcon size={'32px'} round />
						</FacebookShareButton>
						<WhatsappShareButton
							title={includePaymentUrl}
							disabled={!selectedBuyer}
							url="https://web.whatsapp.com/"
							onClick={() => trackShareClick('whatsapp')}
						>
							<WhatsappIcon size={'32px'} round />
						</WhatsappShareButton>
						<TwitterShareButton
							url="https://twitter.com/messages/compose"
							disabled={!selectedBuyer}
							title={includePaymentUrl}
							onClick={() => trackShareClick('twitter')}
						>
							<TwitterIcon size={'32px'} round />
						</TwitterShareButton>
					</div>
				</>
			)}
			<p className="mt-8 text-[12px] text-center">
				You can continue shopping while you track your payment{' '}
				<a
					target="_blank"
					href={`${process.env.REACT_APP_COBUYR_TRACKER}/track?id=${responseId}&pK=${publicKey}`}
					className="text-blue-200 underline"
					onClick={() => {
						postParentAnalyticsEvent('cobuyr_tracker_link_clicked', analyticsContext);
					}}
				>
					here
				</a>
			</p>
		</div>
	);
});
